import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"

import { patchColumnData } from "app/static/frontend/sheets/sheet-action-creators"

import { PublicOrganizationSelect } from "app/static/frontend/selects/containers/PieSelect"
import { CellWrapper } from "app/static/frontend/sheets/components/cells/MantineCells/CellWrapper"

/**
 * Renders the cell containing the contacts organizations. If the cell is in
 * edit mode it renders a Select for choosing orgs to remove / add, otherwise
 * it renders a string of public orgs.
 */
const PublicOrganizationCellComponent = ({ cellValue, columnId, editing, rowId, sheetId, row, ...props }) => {
    const idsFromCellData = cellValue?.data ? cellValue.data.map((d) => d.id) : cellValue?.map((d) => d.id)
    const [localOrgIds, setLocalOrgIds] = useState([])
    /**
     * Callback function for PublicOrganizationSelect changes.  Optimistically
     * renders the addition or deletion of public orgs by updating local state and
     * then making a patch request after. If the request is unsuccessful, the local
     * state is rolled back.
     *
     * @param {Array<Object>} orgs - an array of orgs the associated with the contact
     */
    const handleChange = (orgs) => {
        // grab a copy of the current state in case we need to roll back
        const originalState = [...(localOrgIds || [])]
        const newIds = orgs.map((org) => org.id)

        setLocalOrgIds(newIds)

        try {
            props.patchColumnData({
                id: sheetId,
                columnId,
                rowId,
                newValue: orgs,
                row,
            })
        } catch (error) {
            setLocalOrgIds(originalState)
        }
    }

    /**
     * To handle setting the state with the public org ids on mount
     */
    useEffect(() => setLocalOrgIds(idsFromCellData), [])

    const cellValuesToDisplay = cellValue?.cell_value || cellValue?.map((d) => d?.name_with_parent).join(", ")

    return editing ? (
        <CellWrapper truncate={false} width="100%" {...props}>
            <PublicOrganizationSelect multi onChange={handleChange} value={localOrgIds} />
        </CellWrapper>
    ) : (
        <CellWrapper {...props}>{cellValuesToDisplay}</CellWrapper>
    )
}

PublicOrganizationCellComponent.propTypes = {
    /** the value the cell renders */
    cellValue: PropTypes.string.isRequired,
    /** the column id the cell is associated with */
    columnId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    /** whether or not the cell is in edit mode */
    editing: PropTypes.bool,
    /** handles the patch request for adding or deleting column data */
    patchColumnData: PropTypes.func,
    /** a string consisting of public orgs associated with a contact seperated by commas */
    publicOrgIds: PropTypes.string,
    /** a string consisting of public orgs associated with a contact seperated by commas */
    idsFromCellData: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    /** the row id the cell is associated with */
    rowId: PropTypes.number.isRequired,
    /** the sheet id the cell is associated with */
    sheetId: PropTypes.string.isRequired,
}

export const PublicOrganizationCell = connect(null, { patchColumnData })(PublicOrganizationCellComponent)
